import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { AppShell } from '@web/components/layout/BottomNav'
import { CoursePage } from '@web/features/course/CoursePage'
import { DashboardPage } from '@web/features/dashboard/DashboardPage'
import { AdminPage } from '@web/features/admin/AdminPage'
import { PhrasebookPage } from '@web/features/phrasebook/PhrasebookPage'
import { ProgressPage } from '@web/features/progress/ProgressPage'
import { flushAttemptQueue } from '@web/lib/attempts'
import { LessonPlayer } from '@web/features/lesson/LessonPlayer'
import { LoginPage } from '@web/features/auth/LoginPage'
import { PlacementTest } from '@web/features/placement/PlacementTest'
import { ReviewSession } from '@web/features/review/ReviewSession'
import { AudioProvider } from '@web/lib/audio'
import { AuthProvider, useAuth } from '@web/lib/auth'

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Playback lives above the router: the review screen plays clips by
            id and would otherwise fall through to the no-op default context. */}
        <AudioProvider>
          <Shell />
        </AudioProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}

function Shell() {
  const { user, isLoading } = useAuth()

  // Anything answered offline is flushed once she signs in and whenever the
  // connection returns. Attempts carry client-generated ids, so flushing more
  // often than strictly necessary is harmless.
  useEffect(() => {
    if (!user) return
    void flushAttemptQueue()
    const onOnline = () => void flushAttemptQueue()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [user])

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span
          aria-label="Loading"
          className="size-8 animate-spin rounded-full border-3 border-brand-600 border-t-transparent"
        />
      </div>
    )
  }

  // One gate for the whole app rather than a guard per route: every screen
  // behind it needs a user, and a single check cannot be forgotten on a new
  // route later.
  if (!user) return <LoginPage />

  return (
    <Routes>
      {/* Tabbed screens share the bottom nav. The lesson player and the
          placement test are full-screen on purpose — a nav bar during a
          question is an invitation to wander off mid-lesson. */}
      <Route
        path="/"
        element={
          <AppShell>
            <DashboardPage />
          </AppShell>
        }
      />
      <Route
        path="/course"
        element={
          <AppShell>
            <CoursePage />
          </AppShell>
        }
      />
      <Route path="/admin" element={<AdminPage />} />
      <Route
        path="/phrasebook"
        element={
          <AppShell>
            <PhrasebookPage />
          </AppShell>
        }
      />
      <Route
        path="/progress"
        element={
          <AppShell>
            <ProgressPage />
          </AppShell>
        }
      />
      <Route path="/lesson/:lessonId" element={<LessonPlayer />} />
      {/* Review runs full-screen without the nav, like a lesson: a session
          should be finished, not wandered away from. */}
      <Route path="/review" element={<ReviewSession />} />
      <Route path="/placement" element={<PlacementTest />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
